import { useState, useEffect, useCallback, useRef } from "react";
import { getCurrentSessionInfo } from "@/lib/rpc/auth/session";
import { getMyProfile } from "@/lib/rpc/user/profile";
import { useToast } from "@/hooks/use-toast";
import { ProfileSummary } from "@/types/profile";
import { ProfileSummarySchema } from "@/types/profile.schema";
import { secureStorage } from "@/lib/security/dataProtection";

const LS_CID = "liquid.customer_id";
const LS_NAME = "liquid.profile_name";

interface ProfileData {
  customerId: string | null;
  bridgeCustomerId?: string | null; // Backward compatibility alias
  fullName: string;
  email: string;
  kycStatus: string | null;
  tosStatus: string | null;
  loading: boolean;
}

export const useProfileData = () => {
  const { toast } = useToast();
  const [data, setData] = useState<ProfileData>({
    customerId: null,
    fullName: "",
    email: "",
    kycStatus: null,
    tosStatus: null,
    loading: true
  });
  const [refreshKey, setRefreshKey] = useState(0);

  // ✅ CRITICAL FIX: Stable toast ref to prevent infinite loop
  const toastRef = useRef(toast);
  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);

  const loadProfileData = useCallback(async () => {
    setData(prev => ({ ...prev, loading: true }));

    try {
      // E2E Testing Bypass
      if (typeof window !== 'undefined' && window.localStorage.getItem('e2e_admin_bypass') === 'true') {
        const email = window.localStorage.getItem('e2e_user_email') || 'admin@test.com';
        setData({
          customerId: 'test-customer-id',
          bridgeCustomerId: 'test-bridge-customer-id',
          fullName: 'E2E Test User',
          email,
          kycStatus: 'approved',
          tosStatus: 'accepted',
          loading: false
        });
        return;
      }

      // Seed from local cache for instant UI
      try {
        const cachedName = secureStorage.getItem<string>("profile_name");
        const cachedCid = secureStorage.getItem<string>("bridge_customer_id");
        if (cachedName) setData(prev => ({ ...prev, fullName: cachedName }));
        if (cachedCid) setData(prev => ({ ...prev, customerId: cachedCid }));
      } catch {
        /* cache unavailable */
      }

      const sessionRes = await getCurrentSessionInfo();
      const u = sessionRes.ok ? sessionRes.data?.user : null;

      if (!u?.id) {
        setData(prev => ({ ...prev, loading: false }));
        return;
      }

      // Use secure RPC to get all profile data in one call
      const profileRes = await getMyProfile();
      
      if (!profileRes.ok) {
        console.warn("Failed to get profile summary via RPC:", String((profileRes as any).error));
        throw new Error(String((profileRes as any).error));
      }

      let profileData: ProfileSummary | null = null;

      if (profileRes.data) {
        const parsed = ProfileSummarySchema.safeParse(profileRes.data);
        if (parsed.success) {
          profileData = parsed.data as ProfileSummary;
        } else {
          console.warn('[useProfileData] RPC shape mismatch:', parsed.error.flatten());
        }
      }

      if (profileData?.exists) {
        const displayName = profileData.display_name ?? "";
        const email = profileData.email ?? (u.email ?? "");
        const kycStatus = (profileData.kyc_status ?? 'unknown') as ProfileSummary['kyc_status'];
        const tosStatus = (profileData.tos_status ?? 'unknown') as ProfileSummary['tos_status'];
        const customerId = profileData.customer_id; // string | null

        // Cache ONLY strings - avoid null values in localStorage
        try {
          if (typeof customerId === 'string' && customerId.length > 0) secureStorage.setItem("bridge_customer_id", customerId, { sensitive: true });
          if (displayName) secureStorage.setItem("profile_name", displayName, { sensitive: true });
        } catch {
          /* cache unavailable */
        }

        setData({
          customerId: customerId ?? null,
          bridgeCustomerId: null, // Legacy field - always null for security
          fullName: displayName,
          email,
          kycStatus,
          tosStatus,
          loading: false
        });
        return;
      }

      // No unified customer found - use auth data only
      const emailCandidate = u?.email || "";

      setData({
        customerId: null,
        bridgeCustomerId: null, // Backward compatibility
        fullName: u?.email?.split('@')[0] || '', // Use email prefix as fallback name
        email: emailCandidate,
        kycStatus: null,
        tosStatus: null,
        loading: false
      });
    } catch (error) {
      // Profile data loading failed - don't show toast for permission errors (expected for new users)
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      if (!errorMessage.includes("permission denied") && !errorMessage.includes("42501")) {
        toastRef.current({
          title: "Failed to load profile data",
          description: "Please try refreshing the page or check your connection.",
          variant: "destructive",
        });
      }
      
      // For permission errors, try to get user data from auth session
      try {
        const sessionRes = await getCurrentSessionInfo();
        const u = sessionRes.ok ? sessionRes.data?.user : null;
        if (u) {
          const emailCandidate = u?.email || "";
          setData({
            customerId: null,
            bridgeCustomerId: null,
            fullName: u?.email?.split('@')[0] || '', // Use email prefix as fallback name
            email: emailCandidate,
            kycStatus: null,
            tosStatus: null,
            loading: false
          });
        } else {
          setData(prev => ({ ...prev, loading: false }));
        }
      } catch {
        /* session recovery failed */
        setData(prev => ({ ...prev, loading: false }));
      }
    }
  }, []); // ✅ Empty deps - stable forever!

  useEffect(() => {
    loadProfileData();
  }, [loadProfileData, refreshKey]);

  const refresh = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  return {
    ...data,
    refresh
  };
};
